import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Input, Spinner } from "@fluentui/react-components";
import {
  Search20Regular,
  PersonRegular,
  BuildingRegular,
} from "@fluentui/react-icons";
import {
  useDebouncedClientSearch,
  ClientSearchResult,
} from "state/queries/useClientSearch";

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInputContainer = styled.div`
  position: relative;
`;

const SearchIcon = styled(Search20Regular)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.palette.neutralSecondary};
`;

const StyledInput = styled(Input)`
  width: 100%;
  padding-left: 36px;
`;

const SearchResultsPopover = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  border: 1px solid ${({ theme }) => theme.semanticColors.inputBorder};
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
`;

const SearchResultItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) =>
      theme.semanticColors.listItemBackgroundHovered};
  }

  &:active {
    background: ${({ theme }) =>
      theme.semanticColors.listItemBackgroundChecked};
  }
`;

const ClientIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.themeLighterAlt};
  color: ${({ theme }) => theme.palette.themePrimary};
  flex-shrink: 0;
`;

const ClientInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ClientName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.semanticColors.bodyText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClientDetails = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
`;

const LoadingContainer = styled.div`
  padding: 16px;
  text-align: center;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const NoResultsContainer = styled.div`
  padding: 16px;
  text-align: center;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
  font-size: 14px;
`;

const GOSBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  background: ${({ theme }) => theme.palette.greenLight || "#e6f4ea"};
  color: ${({ theme }) => theme.palette.green};
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 8px;
`;

interface ClientSearchInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onClientSelect?: (client: ClientSearchResult) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const ClientSearchInput: React.FC<ClientSearchInputProps> = ({
  value = "",
  onChange,
  onClientSelect,
  placeholder,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState(value);
  const [showResults, setShowResults] = useState(false);

  // Use debounced search hook
  const { data: searchResults, isLoading } = useDebouncedClientSearch(
    searchText,
    300,
    {
      enabled: searchText.trim().length >= 2,
    }
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setSearchText(newValue);
      onChange?.(newValue);
      setShowResults(newValue.trim().length >= 2);
    },
    [onChange]
  );

  const handleClientSelect = useCallback(
    (client: ClientSearchResult) => {
      const displayName = client.isCompany
        ? client.partnerName
        : `${client.firstName || ""} ${client.lastName || ""}`.trim() ||
          client.partnerName;

      setSearchText(displayName);
      onChange?.(displayName);
      onClientSelect?.(client);
      setShowResults(false);
    },
    [onChange, onClientSelect]
  );

  const handleBlur = useCallback(() => {
    // Delay hiding to allow click events on results
    setTimeout(() => setShowResults(false), 200);
  }, []);

  const handleFocus = useCallback(() => {
    if (searchText.trim().length >= 2) {
      setShowResults(true);
    }
  }, [searchText]);

  const getClientDisplayName = (client: ClientSearchResult) => {
    if (client.isCompany) {
      return client.partnerName;
    }
    return (
      `${client.firstName || ""} ${client.lastName || ""}`.trim() ||
      client.partnerName
    );
  };

  const getClientDetails = (client: ClientSearchResult) => {
    const parts: string[] = [];

    // Add partner number (always show if available)
    if (client.partnerNumber) {
      parts.push(`ID: ${client.partnerNumber}`);
    }

    // Add company register number for companies OR personal code for persons
    if (client.isCompany && client.companyRegisterNumber) {
      parts.push(`Reg: ${client.companyRegisterNumber}`);
    }

    // Add resident status if available
    if (client.isResident !== undefined) {
      parts.push(client.isResident ? "Resident" : "Non-resident");
    }

    // Add full address if available (prioritize over just city)
    const addressParts: string[] = [];
    if (client.partnerAddress) {
      if (client.partnerAddress.street)
        addressParts.push(client.partnerAddress.street);
      if (client.partnerAddress.house)
        addressParts.push(client.partnerAddress.house);
      if (client.partnerAddress.city)
        addressParts.push(client.partnerAddress.city);
      if (client.partnerAddress.zipCode)
        addressParts.push(client.partnerAddress.zipCode);
    }
    if (addressParts.length > 0) {
      parts.push(addressParts.join(", "));
    }

    // Add phone if available
    if (client.partnerPhone) {
      const phone = client.partnerPhone;
      const phoneParts: string[] = [];
      if (phone.phoneCountry) phoneParts.push(`+${phone.phoneCountry}`);
      if (phone.phoneArea) phoneParts.push(phone.phoneArea);
      if (phone.phoneNumber) phoneParts.push(phone.phoneNumber);
      if (phoneParts.length > 0) {
        parts.push(`📞 ${phoneParts.join(" ")}`);
      }
    }

    // Add email
    if (client.partnerMail?.emailAddress) {
      parts.push(`✉ ${client.partnerMail.emailAddress}`);
    }

    return parts.join(" • ");
  };

  return (
    <SearchWrapper>
      <SearchInputContainer>
        <SearchIcon />
        <StyledInput
          value={searchText}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={
            placeholder ||
            t(
              "qtg.proposal.clientData.searchPlaceholder",
              "Search by name or code..."
            )
          }
          disabled={disabled}
        />
      </SearchInputContainer>

      {showResults && searchText.trim().length >= 2 && (
        <SearchResultsPopover>
          {isLoading ? (
            <LoadingContainer>
              <Spinner size="tiny" />
              {t("qtg.proposal.clientData.searching", "Searching...")}
            </LoadingContainer>
          ) : searchResults && searchResults.length > 0 ? (
            searchResults.map((client, index) => (
              <SearchResultItem
                key={`${client.partnerID}-${index}`}
                onClick={() => handleClientSelect(client)}
              >
                <ClientIcon>
                  {client.isCompany ? <BuildingRegular /> : <PersonRegular />}
                </ClientIcon>
                <ClientInfo>
                  <ClientName>
                    {getClientDisplayName(client)}
                    <GOSBadge>
                      {t("qtg.proposal.clientData.gos", "GOS")}
                    </GOSBadge>
                  </ClientName>
                  <ClientDetails>{getClientDetails(client)}</ClientDetails>
                </ClientInfo>
              </SearchResultItem>
            ))
          ) : (
            <NoResultsContainer>
              {t("qtg.proposal.clientData.noResults", "No clients found")}
            </NoResultsContainer>
          )}
        </SearchResultsPopover>
      )}
    </SearchWrapper>
  );
};

export default ClientSearchInput;
